import React, { useEffect, useMemo, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DropdownMenu } from "components/common/DropdownMenu";
import { TextInput } from "components/common/TextInput";
import { Spinner } from "components/common/Spinner";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { SpaceDot } from "./SpaceDot";
import {
  spaceColor,
  useVisibleSpacePaths,
} from "@/components/main/stores/orgContextStore";

interface SpacePickerMenuProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  selectedSpaceId: string | null;
  onPick: (spaceId: string) => void;
  title?: string;
  /// Narrows the list to these ids; `null` while they are still loading.
  eligibleSpaceIds?: readonly string[] | null;
  hint?: string;
  emptyLabel?: string;
  placement?: "bottom" | "bottom-end";
}

/// The "CREATE IN" space picker: a filterable flat list with muted ancestor
/// paths, never an indented tree. A space in its deletion window is in the Trash (D52).
export const SpacePickerMenu: React.FC<SpacePickerMenuProps> = ({
  anchorRef,
  isOpen,
  onClose,
  selectedSpaceId,
  onPick,
  title = "Create in",
  eligibleSpaceIds,
  hint,
  emptyLabel = "No spaces match",
  placement = "bottom-end",
}) => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Lit before the keys move, the keys' place reads as a hovered space.
  const [keyed, setKeyed] = useState(false);
  // Flattened over EVERY visible space, so an eligible child keeps the path of
  // an ancestor that isn't itself on offer.
  const all = useVisibleSpacePaths();
  const paths = useMemo(() => {
    if (eligibleSpaceIds === undefined) return all;
    const eligible = new Set(eligibleSpaceIds);
    return all.filter(({ space }) => eligible.has(space.space_id));
  }, [all, eligibleSpaceIds]);
  const loading = eligibleSpaceIds === null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(({ space, ancestors }) =>
      [...ancestors, space.name].some((name) => name.toLowerCase().includes(q)),
    );
  }, [paths, query]);

  const close = () => {
    setQuery("");
    onClose();
  };
  const pick = (spaceId: string) => {
    onPick(spaceId);
    close();
  };
  useEscapeStack(isOpen, close);

  // The selection is where the arrows start, so opening on a chosen space
  // leaves Enter keeping it.
  useEffect(() => {
    if (!isOpen) return;
    const at = filtered.findIndex((p) => p.space.space_id === selectedSpaceId);
    setActive(Math.max(at, 0));
  }, [isOpen, filtered, selectedSpaceId]);
  useEffect(() => {
    if (isOpen) setKeyed(false);
  }, [isOpen]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setKeyed(true);
      setActive((i) => (i + step + filtered.length) % filtered.length);
    } else if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const target = filtered[active];
      if (target) pick(target.space.space_id);
    }
  };

  return (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={close}
      placement={placement}
      style={{ minWidth: "18.75rem" }}
    >
      <div className="flex flex-col p-1">
        <span
          className="px-2 py-1 font-mono text-fg-muted uppercase select-none"
          style={{
            fontSize: "var(--font-size-xx-small)",
            letterSpacing: "0.1em",
          }}
        >
          {title}
        </span>
        {hint && (
          <span className="px-2 pb-1 text-fg-muted-regular-small select-none">
            {hint}
          </span>
        )}
        <TextInput
          value={query}
          onChange={setQuery}
          placeholder="Find a space"
          autoFocus
          ariaLabel="Find a space"
          onKeyDown={onKeyDown}
          className="h-[var(--control-height-sm)] w-full rounded-app border border-border bg-bg-subtle px-2 text-small text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none"
        />
        <div className="mt-1 max-h-72 overflow-y-auto">
          {loading ? (
            <span className="flex items-center gap-2 px-2 py-2 text-fg-muted-regular-small">
              <Spinner size="sm" />
              Loading spaces…
            </span>
          ) : filtered.length === 0 ? (
            <span className="block px-2 py-2 text-fg-muted-regular-small">
              {query.trim() ? "No spaces match" : emptyLabel}
            </span>
          ) : (
            filtered.map(({ space, ancestors }, index) => {
              const isSelected = space.space_id === selectedSpaceId;
              return (
                <button
                  key={space.space_id}
                  onClick={() => pick(space.space_id)}
                  onMouseMove={() => {
                    setActive(index);
                    setKeyed(false);
                  }}
                  className={`flex h-7 w-full min-w-0 items-center gap-2 rounded-app px-2 text-left ${
                    isSelected
                      ? "bg-list-hover"
                      : keyed && index === active
                        ? "bg-list-hover"
                        : "hover:bg-list-hover"
                  }`}
                >
                  <SpaceDot color={spaceColor(space)} />
                  <span className="min-w-0 flex-1 truncate">
                    {ancestors.map((name) => (
                      <span
                        key={name}
                        className="text-small text-fg-muted"
                      >{`${name} / `}</span>
                    ))}
                    <span className="font-medium text-fg">{space.name}</span>
                  </span>
                  {space.sealed && (
                    <span
                      className="flex shrink-0 items-center gap-1 font-mono text-fg-muted uppercase"
                      style={{
                        fontSize: "var(--font-size-xx-small)",
                        letterSpacing: "0.05em",
                      }}
                    >
                      <LockOutlinedIcon sx={{ fontSize: 11 }} />
                      Sealed
                    </span>
                  )}
                  {isSelected && (
                    <CheckIcon
                      sx={{ fontSize: 14 }}
                      className="shrink-0 text-accent"
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </DropdownMenu>
  );
};
